import * as React from "react";

import type { SettingValue } from "api/sapSettings";
import { EmptyState, Sheet, SheetBody, SheetContent, SheetHeader } from "Components/ui";
import { Lock } from "Components/ui/icons";
import { formatRelative } from "utils/format";
import { useSapSettingsData } from "./data";
import { DASHBOARD_GROUPS, NOTIFICATION_GROUPS, PAYEE_GROUPS } from "./fields";
import IntegrationsSection from "./IntegrationsSection";
import SettingsForm from "./SettingsForm";

type Values = Record<string, SettingValue<unknown>>;
const asValues = (section: object | undefined): Values | undefined => section as Values | undefined;

/** The drawer's content; mounted only while it is open, so nothing loads before that. */
const SapSettingsBody: React.FC = () => {
  const data = useSapSettingsData();
  const updatedAt = data.billing.data?.updatedAt || data.system.data?.updatedAt || null;
  const canRead = data.canReadBilling || data.canReadSystem;

  const sections: React.ReactNode[] = [];
  if (data.canReadBilling) {
    sections.push(
      <SettingsForm
        key="payee"
        title="Pay pages and receipts"
        description="Who clients pay and how else they can pay, on the pay pages, reminders and receipts. Leave a field empty to keep it off them."
        groups={PAYEE_GROUPS}
        values={asValues(data.billing.data?.payee)}
        loading={data.billing.isLoading}
        canEdit={data.canEditBilling}
        onSave={data.savePayee.mutateAsync}
        saving={data.savePayee.isLoading}
      />,
      <SettingsForm
        key="notifications"
        title="Billing notifications"
        description="Who hears when a client becomes read-only, and how billing messages are sent."
        groups={NOTIFICATION_GROUPS}
        values={asValues(data.billing.data?.notifications)}
        loading={data.billing.isLoading}
        canEdit={data.canEditBilling}
        onSave={data.saveNotifications.mutateAsync}
        saving={data.saveNotifications.isLoading}
      />
    );
  }
  if (data.canReadSystem) {
    sections.push(
      <SettingsForm
        key="dashboard"
        title="Dashboard"
        description="When the Dashboard calls a tenant slow and when server bars turn amber or red."
        groups={DASHBOARD_GROUPS}
        values={asValues(data.system.data?.dashboard)}
        loading={data.system.isLoading}
        canEdit={data.canEditSystem}
        onSave={data.saveDashboard.mutateAsync}
        saving={data.saveDashboard.isLoading}
      />
    );
  }
  if (data.canEditSystem) {
    sections.push(<IntegrationsSection key="integrations" query={data.integrations} />);
  }

  return (
    <>
      <SheetHeader
        title="SAP settings"
        description={`What clients see on pay pages and receipts, who SAP notifies, and dashboard limits. A value saved here replaces the server's; changes apply at once and are recorded in the audit log.${
          updatedAt ? ` Last saved ${formatRelative(updatedAt)}.` : ""
        }`}
      />
      <SheetBody>
        {canRead ? (
          <div className="divide-y divide-border">
            {sections.map((section, index) => (
              <div
                key={index}
                className={index === 0 ? "pb-8" : index === sections.length - 1 ? "pt-8" : "py-8"}
              >
                {section}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Lock />}
            title="You can't see SAP settings"
            description="They need billing or tenant read access. Ask a super admin if you need it."
          />
        )}
      </SheetBody>
    </>
  );
};

interface SapSettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * SAP's own settings in a drawer on the Settings page: what clients see on pay pages and
 * receipts, who SAP notifies, dashboard limits, and the status of the server's integrations.
 * A saved value wins over the server's env value; every change applies at once and is audited.
 */
const SapSettingsSheet: React.FC<SapSettingsSheetProps> = ({ open, onOpenChange }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent size="xl">
      <SapSettingsBody />
    </SheetContent>
  </Sheet>
);

export default SapSettingsSheet;
